import type { BadgeVariant } from "@fox1ck-org/ui";
import { PayoutModel } from "@gen/tracker/v1/affiliate_network_pb";
import { ConversionStatus } from "@gen/tracker/v1/conversion_pb";
import { OfferStatus, type PayoutConfig } from "@gen/tracker/v1/offer_pb";
import type { TKey } from "@/lib/i18n";
import { DASH, formatAmount } from "@/lib/format";

/**
 * How offers and partners read on screen (boards OfferList, OfferCard,
 * OfferEditor, PartnerCard): status and model words, our statuses, money in
 * the offer's currency, the link's macros. Pure.
 */

// --- labels -----------------------------------------------------------------

export const OFFER_STATUS_VIEW: Record<OfferStatus, { label: TKey; tone: BadgeVariant }> = {
  [OfferStatus.UNSPECIFIED]: { label: "u6.status.active", tone: "neutral" },
  [OfferStatus.ACTIVE]: { label: "u6.status.active", tone: "ok" },
  [OfferStatus.PAUSED]: { label: "u6.status.paused", tone: "neutral" },
  [OfferStatus.CAPPED]: { label: "u6.status.capped", tone: "warn" },
  [OfferStatus.ARCHIVED]: { label: "u6.status.archived", tone: "neutral" },
};

/** The models an offer is paid by, in the order the select offers them. */
export const PAYOUT_MODELS: PayoutModel[] = [
  PayoutModel.CPA,
  PayoutModel.CPL,
  PayoutModel.CPS,
  PayoutModel.REVSHARE,
  PayoutModel.CPC,
  PayoutModel.CPM,
  PayoutModel.CPI,
  PayoutModel.DOI,
  PayoutModel.SOI,
];

const MODEL_CODE: Record<PayoutModel, string> = {
  [PayoutModel.UNSPECIFIED]: DASH,
  [PayoutModel.CPA]: "CPA",
  [PayoutModel.CPL]: "CPL",
  [PayoutModel.CPS]: "CPS",
  [PayoutModel.REVSHARE]: "RevShare",
  [PayoutModel.CPC]: "CPC",
  [PayoutModel.CPM]: "CPM",
  [PayoutModel.CPI]: "CPI",
  [PayoutModel.DOI]: "DOI",
  [PayoutModel.SOI]: "SOI",
};

/** CPA, CPL… — how a buyer writes them in every chat. */
export function modelCode(m: PayoutModel): string {
  return MODEL_CODE[m] ?? DASH;
}

/** «CPA — за подтверждённый заказ»: the select's line for a model. */
export const MODEL_HINT: Partial<Record<PayoutModel, TKey>> = {
  [PayoutModel.CPA]: "u6.model.cpa",
  [PayoutModel.CPL]: "u6.model.cpl",
  [PayoutModel.CPS]: "u6.model.cps",
  [PayoutModel.REVSHARE]: "u6.model.revshare",
  [PayoutModel.CPC]: "u6.model.cpc",
  [PayoutModel.CPM]: "u6.model.cpm",
  [PayoutModel.CPI]: "u6.model.cpi",
  [PayoutModel.DOI]: "u6.model.doi",
  [PayoutModel.SOI]: "u6.model.soi",
};

/** Our statuses a partner word can mean, in the order of the select. */
export const OUR_STATUSES: ConversionStatus[] = [
  ConversionStatus.APPROVED,
  ConversionStatus.HOLD,
  ConversionStatus.REJECTED,
  ConversionStatus.TRASH,
  ConversionStatus.REFUNDED,
];

export const OUR_STATUS_VIEW: Record<ConversionStatus, { label: TKey; tone: BadgeVariant }> = {
  [ConversionStatus.UNSPECIFIED]: { label: "u6.our.unknown", tone: "neutral" },
  [ConversionStatus.APPROVED]: { label: "u6.our.approved", tone: "ok" },
  [ConversionStatus.HOLD]: { label: "u6.our.hold", tone: "neutral" },
  [ConversionStatus.REJECTED]: { label: "u6.our.rejected", tone: "danger" },
  [ConversionStatus.REFUNDED]: { label: "u6.our.refunded", tone: "danger" },
  [ConversionStatus.TRASH]: { label: "u6.our.trash", tone: "warn" },
};

/** A decimal string as money in its currency: «€32,00». «—» without an amount. */
export function money(amount: string | undefined, currency: string | undefined): string {
  if (!amount || !amount.trim()) return DASH;
  const n = Number(amount);
  if (!Number.isFinite(n)) return DASH;
  return formatAmount(n, currency || "USD");
}

/** «CPA 22,00 $»: an offer's payout as a buyer reads it; nothing without an amount. */
export function payoutLine(payout: PayoutConfig | undefined): string | undefined {
  if (!payout?.amount?.amount?.trim()) return undefined;
  return `${modelCode(payout.model)} ${money(payout.amount.amount, payout.amount.currency)}`;
}

// --- the link ----------------------------------------------------------------

/** The chips under the link: what a buyer puts into a partner link most. */
export const LINK_MACROS = [
  "{click_id}",
  "{campaign_id}",
  "{campaign_name}",
  "{offer_id}",
  "{country_code}",
  "{landing_id}",
  "{traffic_source_name}",
  "{fbclid}",
] as const;

/** The macros a link carries, in order: `{click_id}` → `click_id`. */
export function linkMacros(url: string): string[] {
  return [...url.matchAll(/\{([A-Za-z0-9_:.-]{1,80})\}/g)].map((m) => m[1]!);
}

/** «Кап 118 / 150 сегодня» — or nothing when the offer has no daily cap. */
export function capShare(used: bigint | number | undefined, limit: bigint | number | undefined): number | undefined {
  const l = Number(limit ?? 0);
  if (!l) return undefined;
  return Number(used ?? 0) / l;
}
